"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CommunityVibes() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const thumbnails = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/studio_session_drums.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/studio_session_drums.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/studio_session_drums.png-wvc-sizes",
    alt: "Close up of premium drum kit in studio",
    occasion: "Live Tracking: Session A"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/guitar_amp_glow.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/guitar_amp_glow.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/guitar_amp_glow.png-wvc-sizes",
    alt: "Glowing vacuum tubes in a guitar amp",
    occasion: "Tube Warmth"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_rehearsal_energy.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_rehearsal_energy.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/band_rehearsal_energy.png-wvc-sizes",
    alt: "Local band practicing in the garage room",
    occasion: "The Midnight Vultures"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/mixing_desk_hands.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/mixing_desk_hands.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/mixing_desk_hands.png-wvc-sizes",
    alt: "Hands on a mixing console",
    occasion: "Final Mixdown"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/vocal_booth_vibe.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/vocal_booth_vibe.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/vocal_booth_vibe.png-wvc-sizes",
    alt: "Vocalist tracking in a gritty booth",
    occasion: "Raw Vocals"
  }];
  return <section data-section-id="172" id="community-vibes" className="relative overflow-hidden bg-background text-foreground py-24 md:py-32 lg:py-[140px]">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center select-none">
        <span className="font-default font-extrabold text-[20vw] leading-none tracking-tighter rotate-[-12deg] opacity-10 whitespace-nowrap uppercase" style={{
        color: "var(--primary)"
      }}>
          LOUD_LIVE
        </span>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={leftRef} className={`lg:col-span-7 lg:-ml-6 xl:-ml-12 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              <div className="relative rotate-[-2deg] hover:rotate-0 transition-transform duration-0 ease-out" style={{
              filter: "drop-shadow(8px 8px 0px var(--border))"
            }}>
                <div className="overflow-hidden rounded-md bg-card border-2 border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/garage_room_main_stage.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/garage_room_main_stage.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/garage_room_main_stage.png-wvc-sizes" alt="Gritty, vibrant music studio with backline gear and neon lights" loading="lazy" decoding="async" className="w-full h-auto aspect-[3/4] object-cover grayscale hover:grayscale-0 transition-all duration-500" />
                </div>
                {}
                <div aria-hidden="true" className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-6 rotate-[3deg] opacity-80" style={{
                backgroundColor: "var(--primary)"
              }} />
              </div>
              <p className="mt-6 ml-4 font-mono text-sm tracking-widest text-primary uppercase" style={{
              transform: "rotate(-1.5deg)"
            }}>
                Live_Session_042 // The_Basement_Tapes
              </p>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 lg:pt-8 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="space-y-8">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary">
                Community Rehearsal & Tracking
              </p>

              <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-[0.9] tracking-tighter text-foreground uppercase">
                Where the noise
                <br />
                <span className="text-primary">
                  becomes the scene.
                </span>
              </h2>

              <div className="space-y-5 text-base md:text-lg leading-relaxed text-muted-foreground font-default max-w-lg">
                <p>
                  The Garage Room isn&apos;t just a space; it&apos;s a sonic sanctuary for the loud and the restless. 
                  We provide the high-end backline—premium drum kits, vintage tube amps, and a PA that kicks—so 
                  you can focus on the energy of the take.
                </p>
                <p>
                  Built by musicians for the local community, our studio captures the raw grit of a live 
                  performance with professional-grade clarity. No polished egos here, just pure signal 
                  and the drive to create something that rattles the windows.
                </p>
              </div>

              {}
              <div className="pt-2">
                
              </div>

              {}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-4">
                <Button asChild size="lg" className="font-default font-bold tracking-wide rounded-md" style={{
                backgroundColor: "var(--primary)",
                color: "var(--background)"
              }}>
                  <Link to="/BookPage">Check Availability</Link>
                </Button>
                <Link to="/TheScenePage" className="group inline-flex items-center gap-2 text-foreground font-mono text-xs tracking-widest uppercase border-b-2 border-primary/30 hover:border-primary pb-1 transition-colors">
                  Enter the Gallery
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {}
        <div aria-hidden="true" className="hidden md:block absolute bottom-0 right-6 lg:right-10">
          <div className="relative w-32 h-32 lg:w-36 lg:h-36 rounded-lg flex items-center justify-center" style={{
          animation: "spin 30s linear infinite"
        }}>
            <svg viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <path id="circle-path" d="M 100, 100 m -75, 0 a 75,75 0 1,1 150,0 a 75,75 0 1,1 -150,0" fill="none" />
              </defs>
              <text style={{
              fontFamily: "var(--font-mono)",
              fontSize: "11px",
              letterSpacing: "0.35em",
              textTransform: "uppercase",
              fill: "var(--foreground)"
            }}>
                <textPath href="#circle-path" startOffset="0">
                  REHEARSE &middot; TRACK &middot; MIX &middot; REPEAT &middot;&nbsp;
                </textPath>
              </text>
            </svg>
            <div className="absolute inset-0 m-auto w-3 h-3 rounded-md" style={{
            backgroundColor: "var(--primary)"
          }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>;
}